import { useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { jwtDecode } from "jwt-decode";
import { useLogger, useConfig } from '@gplibraries/react/hooks';
import { InteractionStatus } from '@azure/msal-browser';
/**
 * Returns a function for acquiring an access token.
 *
 * If silent acquisition fails due to interaction requirements, triggers a redirect.
 */
export function useTokenProvider(options) {
    const { instance, accounts, inProgress } = useMsal();
    const logger = useLogger();
    const redirectUri = useConfig('redirectUri');
    const isReady = accounts.length > 0 && inProgress === InteractionStatus.None;
    const getToken = useCallback(async () => {
        const account = instance.getActiveAccount() ?? undefined;
        try {
            const result = await instance.acquireTokenSilent({
                ...options,
                account: account,
                redirectUri: options?.redirectUri || redirectUri || `${window.location.origin}/auth-blank.html`,
            });
            const token = {
                ...result,
                // by default, only the decoded ID token claims are returned, but we want easy access to access token claims too.
                accessTokenClaims: jwtDecode(result.accessToken)
            };
            return token;
        }
        catch (error) {
            if (error?.errorCode === "interaction_required" ||
                error?.errorCode === "login_required" ||
                error?.errorCode === "consent_required" ||
                error.constructor.name === 'InteractionRequiredAuthError') {
                logger.warn('Silent token acquisition failed, interaction required.', {
                    source: 'useTokenProvider',
                    error: {
                        code: error.errorCode,
                        message: error.message,
                        type: error.name,
                    }
                });
                await instance.acquireTokenRedirect({ account, ...options });
                // Return a never-resolving promise to halt execution while the page redirects
                return new Promise(() => { });
            }
            logger.error(error, {
                source: 'useTokenProvider',
                stack: error.stack
            });
            throw error;
        }
        ;
    }, [instance, options, logger]);
    return isReady ? getToken : null;
}
